import { type DiscountTier } from '../../containers/Cart/Checkout/helpers/useCartProduct';

/**
 * Returns the tier whose quantity range contains `activeQty`, or null when no
 * quantity is given or none of the tiers match. A tier with `max_qty === null`
 * is treated as unbounded (the top tier).
 */
const findActiveTier = (
  tiers: DiscountTier[],
  activeQty?: number,
): DiscountTier | null => {
  if (activeQty == null || !Array.isArray(tiers)) {
    return null;
  }

  return (
    tiers.find(
      (tier) => activeQty >= tier.min_qty &&
        (tier.max_qty === null || activeQty <= tier.max_qty),
    ) ?? null
  );
};

/**
 * Returns the unit price discounted by the active tier's percentage, or null
 * when no tier applies. Volume discounts are always a percentage, so the
 * discounted price can be derived client-side from the tier and quantity.
 *
 * @param {number} price - The original (undiscounted) unit price.
 * @param {DiscountTier[]} tiers - The available volume discount tiers.
 * @param {number} [quantity] - The current quantity.
 * @returns {number|null} The discounted price, or null when no tier is active.
 */
export const getDiscountedPrice = (
  price: number,
  tiers: DiscountTier[],
  quantity?: number,
): number | null => {
  const tier = findActiveTier(tiers, quantity);
  return tier ? price * (1 - tier.percentage / 100) : null;
};

export default findActiveTier;
